<template>
  <v-card>
    <v-card-title>
      {{ $t('payment.milestone.partsTitle', { name: payment.name }) }}
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="3">
          <ChartPie
            :items="items"
            :sizes="{ lg: 250, md: 200 }"
            :width="25"
            hide-legend
            hide-summary
          />
        </v-col>
        <v-col>
          <SubMilestoneValueRow
            v-for="(v, index) in $v.form.$each.$iter"
            :validate="v"
            :payment-name="payment.name"
            :edit-mode="!!payment.id"
            :key="index"
            v-model="items[index]"
            :color="items[index].color"
            @input="handleRowChange"
            :is-variation="!!payment.variationId"
            :nameDuplicateError="nameDuplicateError"
            :parts="parts"
            @set-error="v => rowsErrors[index] = v"
            :isMultiCurrency="isMultiCurrency"
          />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        rounded
        dense
        small
        outlined
        color="primary"
        @click="handleAddPart"
        :disabled="payment.multiCurrencyMilestone && !!payment.variationId"
      >
      <v-icon>mdi-plus</v-icon>
      {{ $t('payment.milestone.addPart') }}
      </v-btn>
      <v-btn
        rounded
        dark
        dense
        small
        outlined
        color="primary"
        @click="cancel"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton small @click="submit" :disabled="!items.length" />
    </v-card-actions>
  </v-card>
</template>

<script>
import { subPaymentMilestonesModel } from '@/validators'
import cloneDeep from 'lodash/cloneDeep'
import ColorsMixin from '@/mixins/ColorsMixin'
import { calculateApplicationMilestoneValues } from '@/services'
import { EMPTY_PAYMENT_SUBMILESTONE } from '@/helpers'

export default {
  name: 'SubMilestonesValuesForm',
  props: {
    payment: {
      type: Object,
      default: () => ({})
    },
    parts: Array,
    paymentApplications: Array,
    currencies: Array,
    isMultiCurrency: Boolean
  },
  mixins: [ColorsMixin],
  data () {
    return {
      form: this.payment.subMilestones ? cloneDeep(this.payment.subMilestones) : [],
      rowsErrors: this.payment.id ? this.payment.subMilestones.map(i => false) : []
    }
  },
  computed: {
    items () {
      const itemsSum = this.form.reduce((sum, item) => sum + (item.paymentAmountCents * (item.part.currencyData ? item.part.currencyData.value : 1)), 0)
      return this.form.map((item, index) => {
        const paymentSum = this.payment.paymentAmountCents ? this.payment.paymentAmountCents.sum : itemsSum
        return {
          ...item,
          index,
          part: this.parts.find(i => i.id === item.partId),
          value: (item.paymentAmountCents * item.currency.value) / paymentSum * 100,
          color: this.colors[index % this.colors.length],
          originalPaymentAmountCents: this.payment.id && this.payment.subMilestones[index] ? this.payment.subMilestones[index].paymentAmountCents : 0,
          ...calculateApplicationMilestoneValues(this.paymentApplications, item, this.currencies)
        }
      })
    }
  },
  methods: {
    nameDuplicateError (name) {
      const names = this.form.filter(i => i.name === name)
      return names.length > 1 ? this.$t('payment.milestone.isDuplicated') : null
    },
    handleRowChange (item) {
      const currentPartId = this.form[item.index].part ? this.form[item.index].part.id : null
      const newPartId = item.part ? item.part.id : null
      if (currentPartId !== newPartId) {
        item.currency = item.part.currencyData
        item.partId = item.part.id
      }
      Object.assign(
        this.form[item.index],
        item
      )
    },
    handleAddPart () {
      const maxLen = 10 - `.${this.form.length + 1}`.length
      const name = `${this.payment.name.substring(0, maxLen)}.${this.form.length + 1}`
      this.rowsErrors.push(false)
      this.form.push({
        ...EMPTY_PAYMENT_SUBMILESTONE,
        parentMilestoneId: this.payment.id,
        amountAssignedToApplication: 0,
        amountPaid: 0,
        index: this.form.length,
        name,
        part: { ...this.parts[0] },
        partId: this.parts[0].id,
        paymentAmountCents: 0,
        currency: this.parts[0].currencyData
      })
    },
    cancel () {
      this.form = this.payment.subMilestones ? cloneDeep(this.payment.subMilestones) : []
      this.$v.form.$reset()
      this.$emit('cancel')
      this.$eventBus.$emit('CLOSE_MODAL')
    },
    async submit () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid && this.rowsErrors.filter(i => i).length === 0) {
        this.$emit('saveSubPayments', this.form)
        this.$eventBus.$emit('CLOSE_MODAL')
        this.$v.form.$reset()
        this.form = this.payment.subMilestones ? cloneDeep(this.payment.subMilestones) : []
      }
    }
  },
  watch: {
    'payment.submilestones': {
      deep: true,
      handler (v) {
        this.form = cloneDeep(v)
      }
    }
  },
  validations: subPaymentMilestonesModel
}
</script>
